import * as Sentry from '@sentry/browser';
import { ApiFetchError, buildURL, fetchRequest } from 'Utils/api-fetch-request';
import createVideoItem, { markVideoItemAsNew } from 'Utils/video-item';
import enableRadioboxGroupToggle from 'Utils/vm-radiobox';
import LoadingController from 'Utils/loading';
import Pagination from 'Utils/pagination';

const __CONTEXT__ = {
    longThumbnail: window.__VUMEDI_TERM_DETAIL_CONTEXT__.static.longThumbnail,
    browseSlug: window.__VUMEDI_TERM_DETAIL_CONTEXT__.browseSlug,
    viewedUUID: window.__VUMEDI_TERM_DETAIL_CONTEXT__.viewedUUID,
};

class VideoList {
    constructor(rootSelector, requestBase) {
        this.requestBase = requestBase;
        this.root = document.querySelector(rootSelector);
        this.loadingController = new LoadingController(rootSelector);
        this.messageContainer = this.root.querySelector('.js_message');
    }

    getVideos(params) {
        return new Promise((resolve, reject) => {
            fetchRequest(buildURL(this.requestBase, params))
                .then((data) => {
                    if (!data?.results?.length) {
                        reject(new Error(`${this.requestBase}: empty payload`));
                        return;
                    }

                    resolve({
                        results: data.results,
                        count: data.count,
                    });
                })
                .catch((error) => reject(error));
        });
    }

    load(params) {
        return new Promise((resolve) => {
            this.clear();
            this.loadingController.start();
            this.messageContainer.innerText = '';

            this.getVideos(params)
                .then((data) => {
                    this.render(data.results);
                    resolve(data.count);
                })
                .catch((error) => {
                    let message;

                    if (error.message.includes('empty payload')) {
                        message = 'There are no available videos.';
                    } else if (error instanceof ApiFetchError && error.status === 401) {
                        message = 'Please log in to proceed.';
                    } else {
                        Sentry.captureException(error);
                        message = 'Oops, something went wrong.';
                    }

                    this.messageContainer.innerText = message;
                    resolve(0);
                })
                .finally(() => {
                    this.loadingController.stop();
                });
        });
    }

    clear() {
        const videos = this.root.querySelectorAll('.disease-column-page__video');

        videos?.forEach((el) => {
            this.root.removeChild(el);
        });
    }

    render(videos) {
        for (const video of videos) {
            const videoItem = createVideoItem('long', video, {
                long: {
                    thumbnailPlaceholder: __CONTEXT__.longThumbnail,
                    includeFeaturing: true,
                    includeThumbups: false,
                    showAuthor: true,
                },
            });

            if (videoItem) {
                const videoContent = document.createElement('div');
                videoContent.classList.add('disease-column-page__video');
                videoContent.appendChild(videoItem);
                this.root.appendChild(videoContent);
            }
        }
    }
}

function main() {
    const state = (() => {
        const url = new URL(window.location.href);

        return {
            count: 0,
            filter: url.searchParams.get('filter') || null,
            filterType: url.searchParams.get('filter-type') || null,
            limit: 25,
            page: parseInt(url.searchParams.get('page'), 10) || 1,
            sort: url.searchParams.get('sort') || '-created',
            highlights: [],
        };
    })();

    const dom = {
        filtersContainer: document.querySelector('.js_filtersContainer'),
        filtersClear: document.querySelector('.js_clearFilters'),
        filtersClearInline: document.querySelector('.js_clearFiltersInline'),
        filtersHide: document.querySelector('.js_hideFilters'),
        filtersShow: document.querySelector('.js_showFilters'),
        filterOptions: document.querySelectorAll('[name="filter-option"]'),
        sortActive: document.querySelector('.js_sortActive'),
        sortOptions: document.querySelectorAll('.js_sortOption'),
        videos: document.querySelectorAll('.js_video'),
        title: document.querySelector('.js_title'),
        keyPracticeSection: document.querySelector('.js_keyVideosSection'),
    };

    const videoList = new VideoList('.js_videosList', '/api/videos/');
    const keyVideoList = dom.keyPracticeSection ? new VideoList('.js_keyVideosList', '/api/videos/') : null;
    const pagination = new Pagination('.js_pagination');

    const setMobileFilters = () => {
        document.querySelectorAll('.vm-radiobox-group').forEach((el) => {
            el.classList.add('vm-radiobox-group--flat');
            el.setAttribute('data-state', 'open');
        });
    };

    const setDesktopFilters = () => {
        document.querySelectorAll('.vm-radiobox-group').forEach((el) => {
            el.classList.remove('vm-radiobox-group--flat');
            el.setAttribute('data-state', 'open');
        });
    };

    const hideFilters = (ev) => {
        ev.preventDefault?.();
        dom.filtersContainer.classList.remove('visible');
        document.body.style.overflow = '';
    };

    const showFilters = (ev) => {
        ev.preventDefault?.();
        dom.filtersContainer.classList.add('visible');
        document.body.style.overflow = 'hidden';
    };

    const setActiveSortTitle = () => {
        const sortOption = Array.from(dom.sortOptions).find((el) => el.dataset.param === state.sort);
        dom.sortActive.innerText = sortOption.innerText;
    };

    const setURLParameters = () => {
        const url = new URL(window.location.href);
        let changed = false;

        if (url.searchParams.get('filter') !== state.filter) {
            if (state.filter === null) {
                url.searchParams.delete('filter');
                url.searchParams.delete('filter-type');
            } else {
                url.searchParams.set('filter', state.filter);
                url.searchParams.set('filter-type', state.filterType);
            }

            changed = true;
        }

        if (url.searchParams.get('sort') !== state.sort) {
            url.searchParams.set('sort', state.sort);
            changed = true;
        }

        if (url.searchParams.get('page') !== state.page) {
            url.searchParams.set('page', state.page);
            changed = true;
        }

        if (changed) {
            window.history.replaceState({}, '', url);
        }
    };

    const loadVideos = () => {
        setURLParameters();
        dom.title.dataset.count = '';

        const params = {
            limit: state.limit,
            offset: state.limit * (state.page - 1),
            browse_with_descendants: __CONTEXT__.browseSlug,
            ordering: state.sort,
        };

        if (state.filter) {
            if (state.filterType === 'presentation-type') {
                // We need to use filter name when sending an API request, slug doesn't exist in the database
                params.learning_journey_step = encodeURIComponent(dom.title.innerText);
            }
            if (state.filterType === 'browse-term') {
                params.browse = state.filter;
            }
        }

        pagination.hide();
        videoList.load(params).then((count) => {
            state.count = count;
            pagination.update({
                limit: state.limit,
                page: state.page,
                count: state.count,
            });

            dom.title.dataset.count = state.filter ? `(${count})` : '';
        });
    };

    const retrieveKeyPracticeHighlights = () =>
        new Promise((resolve) => {
            const params = {};

            if (__CONTEXT__.viewedUUID) {
                params.viewed_uuid = __CONTEXT__.viewedUUID;

                fetchRequest(buildURL('/api/highlighted-disease-pages/', params))
                    .then((data) => {
                        if (!data.results?.length) {
                            resolve();
                            return;
                        }

                        const term = data.results.find((el) => el.url === window.location.pathname);
                        state.highlights = term.unseen_key_practice_update_videos;
                        resolve();
                    })
                    .catch((error) => {
                        if (!(error instanceof ApiFetchError) || error.status !== 401) {
                            Sentry.captureException(error);
                        }

                        resolve();
                    });
            } else {
                Sentry.captureMessage('viewedUUID missing – skipping key practice highlights', {
                    level: 'info',
                    tags: { component: 'retrieveKeyPracticeHighlights' },
                    extra: { context: __CONTEXT__ },
                });
                resolve();
            }
        });

    const loadKeyPracticeUpdateVideos = () => {
        if (!dom.keyPracticeSection) {
            return;
        }

        dom.keyPracticeSection.classList.remove('hidden');
        keyVideoList
            .load({
                browse_with_descendants: __CONTEXT__.browseSlug,
                key_practice_update: true,
                ordering: '-created',
            })
            .then(() => {
                if (!state.highlights?.length) {
                    return;
                }

                for (const video of state.highlights) {
                    const videoElement = document.querySelector(`.video-item[data-id="${video.id}"]`);

                    if (videoElement) {
                        markVideoItemAsNew(videoElement);
                    }
                }
            });
    };

    const hideKeyPracticeUpdateVideos = () => {
        if (!dom.keyPracticeSection) {
            return;
        }

        dom.keyPracticeSection.classList.add('hidden');
    };

    const handleFilterUI = (filtered = false) => {
        if (filtered) {
            dom.filtersClear.classList.remove('vm-button--hidden');
            dom.filtersClearInline.classList.remove('vm-button--hidden');

            dom.filtersShow.querySelector('span').innerText = 'Filtered';
            dom.filtersShow.querySelector('.icon').classList.remove('icon-filters');
            dom.filtersShow.querySelector('.icon').innerText = '1';
        } else {
            dom.filtersClear.classList.add('vm-button--hidden');
            dom.filtersClearInline.classList.add('vm-button--hidden');

            dom.filtersShow.querySelector('span').innerText = 'Filters';
            dom.filtersShow.querySelector('.icon').classList.add('icon-filters');
            dom.filtersShow.querySelector('.icon').innerText = '';
        }
    };

    const handleFilterChange = (ev) => {
        const targetId = ev.target.getAttribute('id') ?? null;

        dom.title.innerText = ev.target.dataset.display;

        if (targetId === null) {
            dom.filterOptions.forEach((el) => {
                el.checked = false;
            });

            handleFilterUI(false);
        } else {
            handleFilterUI(true);
        }

        hideFilters({});
        window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });

        state.filter = targetId;
        state.filterType = ev.target.dataset.type || null;
        state.page = 1;

        loadVideos();

        if (state.page === 1 && state.filter === null) {
            loadKeyPracticeUpdateVideos();
        } else {
            hideKeyPracticeUpdateVideos();
        }
    };

    const handleSortChange = (ev) => {
        ev.preventDefault();

        state.page = 1;
        state.sort = ev.target.dataset.param;

        setActiveSortTitle();
        loadVideos();
    };

    const initialize = () => {
        const initialFilter = Array.from(dom.filterOptions).find((el) => el.getAttribute('id') === state.filter);
        const initialFilterType = Array.from(dom.filterOptions).find((el) => el.dataset.type === state.filterType);
        const initialPage = state.page;
        const initialSort = Array.from(dom.sortOptions).find((el) => el.dataset.param === state.sort);

        if (initialFilter && initialFilterType) {
            initialFilter.setAttribute('checked', true);
            dom.title.innerText = initialFilter.dataset.display;
            handleFilterUI(true);
        } else {
            state.filter = null;
            state.filterType = null;
        }

        if (initialPage < 1) {
            state.page = 1;
        }

        if (!initialSort) {
            state.sort = '-created';
        }

        setActiveSortTitle();
        loadVideos();

        if (dom.keyPracticeSection && state.page === 1 && !initialFilter) {
            loadKeyPracticeUpdateVideos();
        }

        if (window.innerWidth > 991) {
            setDesktopFilters();
        } else {
            setMobileFilters();
        }

        window.addEventListener('resize', () => {
            if (window.innerWidth > 991) {
                hideFilters({});
                setDesktopFilters();
            } else {
                setMobileFilters();
            }
        });
    };

    dom.filtersHide.addEventListener('click', hideFilters);
    dom.filtersShow.addEventListener('click', showFilters);
    dom.filtersClear.addEventListener('click', (ev) => {
        ev.preventDefault();
        handleFilterChange({ target: dom.filtersClear });
    });
    dom.filtersClearInline.addEventListener('click', (ev) => {
        ev.preventDefault();
        handleFilterChange({ target: dom.filtersClearInline });
    });

    dom.filterOptions.forEach((el) => el.addEventListener('change', handleFilterChange));
    dom.sortOptions.forEach((el) => el.addEventListener('click', handleSortChange));

    pagination.onClick = (page) => {
        state.page = page;
        loadVideos();

        if (!dom.keyPracticeSection) {
            return;
        }

        if (page === 1) {
            loadKeyPracticeUpdateVideos();
        } else {
            hideKeyPracticeUpdateVideos();
        }
    };

    if (dom.keyPracticeSection) {
        retrieveKeyPracticeHighlights().then(initialize);
    } else {
        initialize();
    }

    enableRadioboxGroupToggle();
}

window.addEventListener('DOMContentLoaded', main);
